import { useCallback, useMemo } from 'react';
import { useHasPermission, useHasProjectPermissionCallback } from 'hooks/use-has-permission';
import { useCanManageCustomFlows, useManageCustomFlowsPermission } from 'hooks/use-can-manage-custom-flows';

export const PROJECT_SETTINGS_MENU_KEYS = [
  'general',
  'users',
  'teams',
  'credentials',
  'policies',
  'notifications'
] as const;

export type ProjectSettingsMenuKey = (typeof PROJECT_SETTINGS_MENU_KEYS)[number];

const CUSTOM_FLOWS_MENU_KEYS: readonly ProjectSettingsMenuKey[] = ['policies'];
const NO_MENU_KEYS: readonly ProjectSettingsMenuKey[] = [];

const selectMenuKeys = ({
  canEditProjectSettings,
  canManageCustomFlows,
  canViewProject
}: {
  canEditProjectSettings: boolean;
  canManageCustomFlows: boolean;
  canViewProject: boolean;
}) => {
  if (canEditProjectSettings) return PROJECT_SETTINGS_MENU_KEYS;
  if (canManageCustomFlows && canViewProject) return CUSTOM_FLOWS_MENU_KEYS;
  return NO_MENU_KEYS;
};

export const useProjectSettingsMenuKeys = (opts: { projectId?: string } = {}) => {
  const { isAuthorized: canEditProjectSettings, isLoading: isLoadingEditProjectSettings } = useHasPermission(
    'EDIT_PROJECT_SETTINGS',
    opts
  );
  const { isAuthorized: canViewProject, isLoading: isLoadingViewProject } = useHasPermission('VIEW_PROJECT', opts);
  const { isAuthorized: canManageCustomFlows, isLoading: isLoadingManageCustomFlows } = useCanManageCustomFlows(opts);

  const isLoading = isLoadingEditProjectSettings || isLoadingViewProject || isLoadingManageCustomFlows;

  const menuKeys = useMemo(
    () => (isLoading ? NO_MENU_KEYS : selectMenuKeys({ canEditProjectSettings, canManageCustomFlows, canViewProject })),
    [isLoading, canEditProjectSettings, canManageCustomFlows, canViewProject]
  );

  return { menuKeys, isLoading };
};

export const useProjectSettingsMenuKeysCallback = () => {
  const { hasProjectPermission, isLoading: isLoadingPermissions } = useHasProjectPermissionCallback();
  const { permission: manageCustomFlowsPermission, isLoading: isLoadingFlag } = useManageCustomFlowsPermission();
  const isLoading = isLoadingPermissions || isLoadingFlag;

  const getMenuKeys = useCallback(
    (projectId: string) =>
      isLoading
        ? NO_MENU_KEYS
        : selectMenuKeys({
            canEditProjectSettings: hasProjectPermission(projectId, 'EDIT_PROJECT_SETTINGS'),
            canManageCustomFlows: hasProjectPermission(projectId, manageCustomFlowsPermission),
            canViewProject: hasProjectPermission(projectId, 'VIEW_PROJECT')
          }),
    [isLoading, hasProjectPermission, manageCustomFlowsPermission]
  );

  return { getMenuKeys, isLoading };
};
